import { useCallback, useEffect, useRef, useState } from "react";
import { type EpisodeFile, type EpisodeSummary } from "@/api";
import { createLogger } from "@/logging";
import { hasVideoUrlExpiredSinceReceipt } from "./episodeVideoUrlExpiry";
import { resolveLoadErrorMessage, type LoadState } from "./loadState";
import type { FileBrowserSource } from "./fileBrowserSource";

const logger = createLogger("components/dataset/episodes");

type ReceivedFiles = {
  source: FileBrowserSource;
  episode: EpisodeSummary;
  data: EpisodeFile[];
  receivedAtMs: number;
  refreshRequested: boolean;
};

// 영상 URL 은 목록 응답에 인라인된다. 오류가 난 URL 이 받은 뒤 만료 구간에 들어갔을 때만
// 목록을 한 번 갱신한다. 새 응답을 기다리는 동안에도 기존 플레이어를 유지한다.
export function useEpisodeFiles(
  source: FileBrowserSource,
  episode: EpisodeSummary | null,
): { files: LoadState<EpisodeFile[]>; reportVideoError: (fileUuid: string) => void } {
  const [files, setFiles] = useState<LoadState<EpisodeFile[]>>({ status: "idle" });
  const requestTokenRef = useRef(0);
  const loadedRef = useRef<ReceivedFiles | null>(null);

  const loadFiles = useCallback(
    async (refresh: boolean): Promise<void> => {
      if (episode === null) return;
      const requestToken = ++requestTokenRef.current;
      if (!refresh) setFiles({ status: "loading" });
      try {
        const data = await source.episodeFiles(episode);
        if (requestToken !== requestTokenRef.current) return;
        // setState 보다 먼저 바꾼다. 그래서 같은 프레임에 늦게 도착한 오류도 새 URL 의 만료 시각을 쓴다.
        loadedRef.current = {
          source,
          episode,
          data,
          receivedAtMs: Date.now(),
          refreshRequested: false,
        };
        setFiles({ status: "success", data });
      } catch (error: unknown) {
        if (requestToken !== requestTokenRef.current) return;
        if (refresh) {
          logger.warn("Episode video URL refresh failed", error);
        } else {
          logger.error("episodeFiles failed", error);
          setFiles({ status: "error", message: resolveLoadErrorMessage(error) });
        }
      }
    },
    [source, episode],
  );

  useEffect(() => {
    loadedRef.current = null;
    if (episode === null) setFiles({ status: "idle" });
    else void loadFiles(false);
    return () => {
      requestTokenRef.current += 1;
      loadedRef.current = null;
    };
  }, [episode, loadFiles]);

  const reportVideoError = useCallback(
    (fileUuid: string): void => {
      const loaded = loadedRef.current;
      // 이미 벗어난 에피소드의 이벤트와 초기 로딩 중의 오류로는 새로 조회하지 않는다.
      if (
        !loaded ||
        loaded.source !== source ||
        loaded.episode !== episode ||
        loaded.refreshRequested
      )
        return;
      const file = loaded.data.find((item) => item.uuid === fileUuid);
      if (!file || file.kind !== "video" || !file.original_url) return;
      if (
        !hasVideoUrlExpiredSinceReceipt(
          file.original_url_expires_at,
          loaded.receivedAtMs,
          Date.now(),
        )
      )
        return;
      // 여러 카메라의 동시 오류를 state 반영보다 먼저 잠근다. 갱신이 실패해도 이 응답으로는 다시 시도하지 않는다.
      loaded.refreshRequested = true;
      void loadFiles(true);
    },
    [source, episode, loadFiles],
  );

  return { files, reportVideoError };
}
